import type { LogAssessmentColumn, LogAssessmentDetail } from './log-assessment.models';

const ACHIEVEMENT_V23_COLUMNS: readonly LogAssessmentColumn[] = [
  {
    category: 'Factors - Captain',
    factors: [
      {
        uiName: 'Manning agent',
        riskPct: '0.70%',
        description: 'Weighted manning agent contribution.',
        changeSource: 'none',
        displayValue: 'LAPA',
      },
      {
        uiName: 'Captain',
        riskPct: '0.70%',
        description: 'Current captain on the voyage.',
        changeSource: 'none',
        displayValue: 'Adam Philips',
        subtitle: 'from 01 Jan 2026 to 01 Feb 2026',
      },
      {
        uiName: 'Captain audit count',
        riskPct: '0.70%',
        description: 'Number of captain audits.',
        changeSource: 'none',
        displayValue: '120',
      },
      {
        uiName: 'Ship audit count',
        riskPct: '0.70%',
        description: 'Number of ship audits on the voyage.',
        changeSource: 'none',
        displayValue: '55',
      },
      {
        uiName: 'Captain training course count',
        riskPct: '0.70%',
        description: 'Captain training courses completed.',
        changeSource: 'none',
        displayValue: '3',
      },
      {
        uiName: 'Captain experience',
        riskPct: '0.70%',
        description: 'Years of captain experience.',
        changeSource: 'none',
        displayValue: '1 year 10 months 4 days',
      },
    ],
  },
  {
    category: 'Factors - Crew',
    factors: [
      {
        uiName: 'Crew family members on board count',
        riskPct: '0.70%',
        description: 'Family members embarked.',
        changeSource: 'user',
        fromValue: '48',
        toValue: '50',
        valueTooltipAvatarUrl: 'https://i.pravatar.cc/32?u=crew-family',
      },
      {
        uiName: 'Average crew training course',
        riskPct: '0.70%',
        description: 'Average training courses per crew member.',
        changeSource: 'auto',
        fromValue: '36',
        toValue: '40',
      },
      {
        uiName: 'Average crew age',
        riskPct: '0.70%',
        description: 'Mean crew age.',
        changeSource: 'user',
        fromValue: '36',
        toValue: '37',
      },
      {
        uiName: 'Average crew experience',
        riskPct: '0.70%',
        description: 'Mean crew experience in years.',
        changeSource: 'none',
        displayValue: '8',
      },
      {
        uiName: 'Unique crew nationalities count',
        riskPct: '0.70%',
        description: 'Distinct nationalities.',
        changeSource: 'none',
        displayValue: '12',
      },
    ],
  },
  {
    category: 'Factors - Ship',
    factors: [
      {
        uiName: 'Ship near miss per voyage days ratio',
        riskPct: '0.16%',
        description: 'Near-miss events per voyage day.',
        changeSource: 'user',
        fromValue: '4:7',
        toValue: '1:2',
      },
      {
        uiName: 'Ship days since last audit',
        riskPct: '0.16%',
        description: 'Days since last ship audit.',
        changeSource: 'none',
        displayValue: '5',
      },
      {
        uiName: 'Ship days since last inspection',
        riskPct: '0.16%',
        description: 'Days since last inspection.',
        changeSource: 'none',
        displayValue: '20',
      },
    ],
  },
  {
    category: 'Other contributing factors',
    factors: [
      {
        uiName: 'Total voyage days estimated',
        riskPct: '2.21%',
        description: 'Estimated total voyage duration.',
        changeSource: 'none',
        displayValue: '123',
      },
      {
        uiName: 'Sea days estimated',
        riskPct: '2.19%',
        description: 'Estimated days at sea.',
        changeSource: 'none',
        displayValue: '123',
      },
      {
        uiName: 'Chemical discharging port count',
        riskPct: '1.02%',
        description: 'Chemical discharging port count.',
        changeSource: 'none',
        displayValue: '123',
      },
      {
        uiName: 'Chemical loading port count',
        riskPct: '1.02%',
        description: 'Chemical loading port count.',
        changeSource: 'none',
        displayValue: '123',
      },
      {
        uiName: 'Port days estimated (days)',
        riskPct: '1.02%',
        description: 'Estimated days in port.',
        changeSource: 'none',
        displayValue: '123',
      },
      {
        uiName: 'Cargo complexity',
        riskPct: '1.02%',
        description: 'Cargo complexity score.',
        changeSource: 'none',
        displayValue: '2',
      },
    ],
  },
];

/** Placeholder until GET logs assessment API exists. */
export const LOG_ASSESSMENT_BY_SHIP_ID: Readonly<Record<string, LogAssessmentDetail>> = {
  'stolt-achievement': {
    shipId: 'stolt-achievement',
    shipName: 'Stolt Achievement',
    updatedByLabel: 'Elle Lee (EEL), 01 Jan 2026, 13:19 UTC',
    overallRiskPct: '27.50%',
    riskCategory: 'High',
    voyageCount: 4,
    totalVoyageDays: 127,
    topFactors: [
      'Captain audit count is too high',
      '2nd top contributing risk factor',
      '3rd top contributing risk factor',
    ],
    voyages: [
      { voyageNo: '23', riskPct: '29%', route: 'Houston -> Rotterdam', voyageLevelRiskCategory: 'High' },
      { voyageNo: '24', riskPct: '17.98%', route: 'Rotterdam -> Singapore', voyageLevelRiskCategory: 'Medium' },
      { voyageNo: '25', riskPct: '29.51%', route: 'Short -> Singapore', voyageLevelRiskCategory: 'High' },
      { voyageNo: '26', riskPct: '12.3%', route: 'Port Klang -> Bintulu', voyageLevelRiskCategory: 'Low' },
    ],
    columnsByVoyage: {
      '23': ACHIEVEMENT_V23_COLUMNS,
      '24': ACHIEVEMENT_V23_COLUMNS,
      '25': ACHIEVEMENT_V23_COLUMNS,
      '26': ACHIEVEMENT_V23_COLUMNS,
    },
  },
  'stolt-capability': {
    shipId: 'stolt-capability',
    shipName: 'Stolt Capability',
    updatedByLabel: 'Elle Lee (EEL), 27 Feb 2026, 15:45 UTC',
    overallRiskPct: '18.20%',
    riskCategory: 'Medium',
    voyageCount: 1,
    totalVoyageDays: 6,
    topFactors: [
      'Ship audit count is elevated',
      'Crew training gap',
      'Port days above fleet average',
    ],
    voyages: [{ voyageNo: '23', riskPct: '22%', route: 'Singapore -> Houston', voyageLevelRiskCategory: 'Medium' }],
    columnsByVoyage: {
      '23': ACHIEVEMENT_V23_COLUMNS,
    },
  },
  'stolt-express': {
    shipId: 'stolt-express',
    shipName: 'Stolt Express',
    updatedByLabel: 'James Chen (JCH), 26 Feb 2026, 09:12 UTC',
    overallRiskPct: '14.10%',
    riskCategory: 'Low',
    topFactors: ['Voyage risk score increased', 'Captain experience below target', 'Cargo complexity'],
    voyages: [{ voyageNo: '12', riskPct: '15%', route: 'Rotterdam -> New York', voyageLevelRiskCategory: 'Low' }],
    columnsByVoyage: {
      '12': ACHIEVEMENT_V23_COLUMNS,
    },
  },
};
